import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Card from "./Card";
import style from "./ViewBid.module.css";
import {
  bidCreateSuccessToast,
  failedBid,
} from "../../components/NotificationToast";
import { toast } from "react-toastify";
import {
  acceptBid,
  fetchBidItems,
  requestBidPriceUpdate,
} from "../../service/bids";

const ViewBid = () => {
  let { id } = useParams();
  const requestedStorageKey = `requested-bids-${id}`;
  const [showSpinner, setShowSpinner] = useState(true);
  const [all, setAll] = useState();
  const [fast, setFast] = useState();
  const [cheap, setCheap] = useState();
  const [acceptingBidId, setAcceptingBidId] = useState(null);
  const [requestingBidId, setRequestingBidId] = useState(null);
  const [requestedBidIds, setRequestedBidIds] = useState({});
  const latestRequestRef = useRef(0);
  const hasHydratedRequestedRef = useRef(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const isTableLoading = showSpinner || !hasLoadedOnce;
  const isCardLoading = showSpinner || !hasLoadedOnce;

  useEffect(() => {
    hasHydratedRequestedRef.current = false;
    try {
      const stored = localStorage.getItem(requestedStorageKey);
      setRequestedBidIds(stored ? JSON.parse(stored) : {});
    } catch (error) {
      setRequestedBidIds({});
    } finally {
      hasHydratedRequestedRef.current = true;
    }
  }, [requestedStorageKey]);

  useEffect(() => {
    if (!hasHydratedRequestedRef.current) return;
    localStorage.setItem(requestedStorageKey, JSON.stringify(requestedBidIds));
  }, [requestedBidIds, requestedStorageKey]);

  useEffect(() => {
    let isActive = true;
    const requestId = ++latestRequestRef.current;

    const getData = async () => {
      setShowSpinner(true);
      try {
        const data = await fetchBidItems(id);
        if (!isActive || requestId !== latestRequestRef.current) return;

        setAll(data?.All);
        setFast(data?.fastest);
        setCheap(data?.cheapest);
      } catch (error) {
        if (isActive && requestId === latestRequestRef.current) {
          console.log("get Bid error", error);
        }
      } finally {
        if (isActive && requestId === latestRequestRef.current) {
          setHasLoadedOnce(true);
          setShowSpinner(false);
        }
      }
    };

    getData();

    return () => {
      isActive = false;
    };
  }, [id]);

  const handleAccept = async (item) => {
    if (!item?.id || acceptingBidId === item.id) return;

    setAcceptingBidId(item.id);
    try {
      const response = await acceptBid({
        bidId: item.id,
        ordertype: item.ordertype,
      });

      if (response?.data?.status === 200) {
        bidCreateSuccessToast();
        setAll((prev) =>
          Array.isArray(prev) ? prev.filter((bid) => bid?.id !== item.id) : prev,
        );
        setFast((prev) => (prev?.id === item.id ? undefined : prev));
        setCheap((prev) => (prev?.id === item.id ? undefined : prev));
        setRequestedBidIds((prev) => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
      } else {
        failedBid();
        console.log("error one");
      }
    } catch (error) {
      console.error("Error accepting bid:", error);
      failedBid();
    } finally {
      setAcceptingBidId(null);
    }
  };

  const hasZeroPriceItems = (item) =>
    String(item?.has_zero_price_items) === "1";

  const handleRequestPriceUpdate = async (item) => {
    if (!item?.id || requestingBidId === item.id) return;

    setRequestingBidId(item.id);
    try {
      const response = await requestBidPriceUpdate(item?.id);

      if (response?.data?.status === 200) {
        setRequestedBidIds((prev) => ({
          ...prev,
          [item.id]: true,
        }));
        toast.success(
          `${item?.name || "Vendor"} has been notified to update prices.`,
        );
      } else {
        toast.warning("Unable to notify vendor. Please try again.");
      }
    } catch (error) {
      console.error("Error notifying vendor for price update:", error);
      toast.warning("Unable to notify vendor. Please try again.");
    } finally {
      setRequestingBidId(null);
    }
  };

  return (
    <div className={style.page}>
      <section className={style.cardSection}>
        <Card
          fast={fast}
          cheap={cheap}
            isLoading={isCardLoading}
          onAccept={handleAccept}
          onRequestPriceUpdate={handleRequestPriceUpdate}
          acceptingBidId={acceptingBidId}
          requestingBidId={requestingBidId}
          requestedBidIds={requestedBidIds}
          hasZeroPriceItems={hasZeroPriceItems}
        />
      </section>

      <section className={style.tableSection}>
        <div className={style.headingRow}>
          <p className={style.title}>Others</p>
          <p className={style.subtitle}>Review and manage all submitted bids</p>
        </div>

        <div className={style.tableWrapper}>
          <table className={style.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Vendor name</th>
                <th>Total cost</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isTableLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    <td>
                      <div className={`${style.skeletonCell} ${style.skeletonShimmer}`}></div>
                    </td>
                    <td>
                      <div className={`${style.skeletonCellWide} ${style.skeletonShimmer}`}></div>
                    </td>
                    <td>
                      <div className={`${style.skeletonCell} ${style.skeletonShimmer}`}></div>
                    </td>
                    <td>
                      <div className={`${style.skeletonCellWide} ${style.skeletonShimmer}`}></div>
                    </td>
                    <td>
                      <div className={style.actionButtons}>
                        <div className={`${style.skeletonBtn} ${style.skeletonShimmer}`}></div>
                        <div className={`${style.skeletonBtnWide} ${style.skeletonShimmer}`}></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : all && all.length > 0 ? (
                all.map((row, index) => (
                  <tr
                    key={index}
                    className={style.rowReveal}
                    style={{ animationDelay: `${120 + index * 45}ms` }}
                  >
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td>{row.cost_rating}</td>
                    <td>{row.created}</td>
                    <td>
                      <div className={style.actionButtons}>
                        <button
                          className={`${style.button} ${acceptingBidId === row?.id ? style.disabledButton : ""}`}
                          onClick={() => handleAccept(row)}
                          disabled={acceptingBidId === row?.id}
                        >
                          {acceptingBidId === row?.id ? "Accepting..." : "Accept"}
                        </button>
                        {hasZeroPriceItems(row) && (
                          <button
                            className={`${style.warningButton} ${requestedBidIds[row?.id] ? style.requestedButton : ""} ${requestingBidId === row?.id ? style.disabledButton : ""}`}
                            onClick={() => handleRequestPriceUpdate(row)}
                            disabled={requestingBidId === row?.id || requestedBidIds[row?.id]}
                          >
                            {requestingBidId === row?.id
                              ? "Requesting..."
                              : requestedBidIds[row?.id]
                                ? "Requested"
                                : "Request Price Update"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={style.emptyState}>
                    Items Not Available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ViewBid;
